import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import LoadingButton from "@mui/lab/LoadingButton";
import { API_BASE_URL } from "../config";
import { useAlert } from "../contexts/AlertProvider";
import { getTokenExpiryMs, saveReturnTo } from "../utils/session";

const WARN_BEFORE_MS = 2 * 60 * 1000;

// Warns shortly before the session token expires and offers to extend it
// (POST /agents/refresh-token/v1). If the server refuses, or the countdown
// runs out, the agent signs in again and is returned to this page.
export default function SessionExpiryWarning() {
  const user = useUser();
  const { id, token, resetUser, setUser } = user;
  const { alert } = useAlert();
  const [refreshing, setRefreshing] = React.useState(false);
  const navigate = useNavigate();
  const [remainingMs, setRemainingMs] = React.useState(null);
  const expiryMs = React.useMemo(() => getTokenExpiryMs(token || ""), [token]);

  const signOut = React.useCallback(() => {
    saveReturnTo(id);
    resetUser({ reason: "expired" });
    navigate("/", { replace: true });
  }, [id, resetUser, navigate]);

  const staySignedIn = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/agents/refresh-token/v1`, { method: "POST" });
      const result = await response.json();

      if (!response.ok || result.status !== "SUCCESS" || !result.data?.token) {
        throw new Error(result.failureReason || "Refresh refused");
      }

      setUser({ ...user, token: result.data.token });
      setRemainingMs(null);
      alert({
        title: "Session Extended",
        detail: "You're signed in for another few hours.",
        severity: "success",
        duration: 4000,
      });
    } catch {
      alert({
        title: "Couldn't Extend Session",
        detail: "Please sign in again to keep working.",
        severity: "warning",
        duration: 8000,
      });
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!expiryMs) return undefined;

    const tick = () => {
      const left = expiryMs - Date.now();
      if (left <= 0) {
        signOut();
        return;
      }
      setRemainingMs(left <= WARN_BEFORE_MS ? left : null);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiryMs, signOut]);

  const open = remainingMs !== null;
  const minutes = Math.floor((remainingMs ?? 0) / 60000);
  const seconds = String(Math.floor(((remainingMs ?? 0) % 60000) / 1000)).padStart(2, "0");

  return (
    <Dialog open={open} aria-labelledby="session-expiry-title">
      <DialogTitle id="session-expiry-title">Your session is about to expire</DialogTitle>
      <DialogContent>
        <DialogContentText role="timer" aria-live="polite">
          You will be signed out in {minutes}:{seconds}. Stay signed in to keep working, or finish
          saving any open forms. If you sign in again you will return to this page.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={signOut} disabled={refreshing}>
          Sign in again
        </Button>
        <LoadingButton variant="contained" loading={refreshing} onClick={staySignedIn}>
          Stay signed in
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
